import * as React from 'react';
import { createPortal } from 'react-dom';

import { Picture } from '@shared/components/helpers';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { SANS_SERIF_FONT_FAMILY } from '@portal/constants/fonts';
import { ImageFragment } from '@portal/schema';

/**
 * Figma — Pre-Existing-Damage-Flags (file `NlkTKV4l9KJIN1m9EUjRpH`):
 * https://www.figma.com/design/NlkTKV4l9KJIN1m9EUjRpH/Pre-Existing-Damage-Flags?node-id=10-815&m=dev
 * Modal spec: nodes `10:889` (backdrop), `11:912` (column), `10:892` (stage), `11:906` (nav), `11:904` (thumbs).
 * Portaled to `document.body`.
 */
const C = {
  overlay: 'rgba(16, 24, 23, 0.8)',
  cloud: '#ffffff',
  teal: '#066b65',
  thumbBg: '#d7d7d7',
  navDisabled: '#bdbdbd',
} as const;

/** Slightly smaller than Figma handoff — one scale for shell, thumbs, nav, and icons. */
const SCALE = 0.88;
/** Figma main stage is 596px tall; this shrinks only the photo area so the dialog is shorter. */
const STAGE_HEIGHT_MULT = 0.72;
const zi = (n: number) => Math.max(1, Math.round(n * SCALE));
const zd = (n: number, decimals = 3) => {
  const m = 10 ** decimals;
  return Math.round(n * SCALE * m) / m;
};

const mainStageH = Math.max(240, Math.round(596 * SCALE * STAGE_HEIGHT_MULT));

/** Figma base → scaled (`11:912` column, `10:892` stage, `11:906` nav, `11:904` thumbs). */
const W = {
  overlayPad: zi(16),
  gap: zi(24),
  thumbW: zi(143),
  thumbH: zi(111),
  thumbR: zd(16.614, 3),
  thumbGap: zd(12.922, 3),
  navCircle: zd(42.757, 3),
  navBetween: zd(46.8, 1),
  mainW: zi(768),
  mainH: mainStageH,
  close: zi(28),
  chevronW: zi(11),
  chevronH: zi(18),
  stageRadius: zi(18),
  /** Small floor for tap target / legibility; kept low so short viewports still fit without scroll. */
  stageFloor: zi(56),
  outline: zi(2),
  outlineOffsetClose: zi(3),
  thumbRing: zi(2),
  thumbFocusOffset: zi(2),
} as const;

const CSS = `
.pedgal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: ${C.overlay};
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100dvh;
  max-height: 100dvh;
  padding: ${W.overlayPad}px;
  padding-top: max(${W.overlayPad}px, env(safe-area-inset-top, 0px));
  padding-bottom: max(${W.overlayPad}px, env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: hidden;
  min-height: 0;
  font-family: ${SANS_SERIF_FONT_FAMILY};
}
.pedgal-shell {
  position: relative;
  width: 100%;
  max-width: ${W.mainW}px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${W.gap}px;
  /* Grow within padded overlay so the stage can shrink; keeps thumbs + nav in view (no vertical scroll). */
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
}
.pedgal-top {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
}
.pedgal-close {
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  line-height: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${W.close}px;
  height: ${W.close}px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  color: ${C.cloud};
}
.pedgal-close:hover { opacity: 0.88; }
.pedgal-close:focus-visible {
  outline: ${W.outline}px solid ${C.cloud};
  outline-offset: ${W.outlineOffsetClose}px;
}
.pedgal-stage {
  background: ${C.cloud};
  border-radius: ${W.stageRadius}px;
  width: 100%;
  max-width: ${W.mainW}px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Only flexible block: shrinks so close + nav + thumbs always fit — no vertical scroll. */
  flex: 1 1 auto;
  min-height: ${W.stageFloor}px;
  max-height: ${W.mainH}px;
  height: auto;
  box-sizing: border-box;
}
.pedgal-stageInner {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}
.pedgal-stageInner img {
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
}
.pedgal-navRow {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: ${W.navBetween}px;
  flex-shrink: 0;
}
.pedgal-navBtn {
  width: ${W.navCircle}px;
  height: ${W.navCircle}px;
  border-radius: 50%;
  border: none;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;
  background: ${C.teal};
  color: ${C.cloud};
}
.pedgal-navBtn:disabled {
  cursor: not-allowed;
  background: ${C.navDisabled} !important;
  color: ${C.cloud};
  opacity: 0.85;
}
.pedgal-navBtn:not(:disabled):hover {
  filter: brightness(1.06);
}
.pedgal-navBtn:focus-visible {
  outline: ${W.outline}px solid ${C.cloud};
  outline-offset: ${W.outlineOffsetClose}px;
}
.pedgal-navBtn svg {
  display: block;
}
.pedgal-thumbs {
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  gap: ${W.thumbGap}px;
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
}
.pedgal-thumb {
  width: ${W.thumbW}px;
  height: ${W.thumbH}px;
  flex: 0 0 auto;
  border-radius: ${W.thumbR}px;
  overflow: hidden;
  border: none;
  padding: 0;
  cursor: pointer;
  box-sizing: border-box;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}
.pedgal-thumb img {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  object-position: center;
  flex-shrink: 0;
}
.pedgal-thumb--active {
  background: ${C.cloud};
  /* Figma: active thumb reads as white tile on strip; light ring reads on dim overlay */
  box-shadow: 0 0 0 ${W.thumbRing}px rgba(255, 255, 255, 0.95);
}
.pedgal-thumb--idle {
  background: ${C.thumbBg};
}
.pedgal-thumb:focus-visible {
  outline: ${W.outline}px solid ${C.cloud};
  outline-offset: ${W.thumbFocusOffset}px;
}
`;

function IconClose(): React.ReactElement {
  const s = W.close;
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={s} height={s} viewBox="0 0 28 28" fill="none" aria-hidden>
      <path
        d="M2.8 28L0 25.2L11.2 14L0 2.8L2.8 0L14 11.2L25.2 0L28 2.8L16.8 14L28 25.2L25.2 28L14 16.8L2.8 28Z"
        fill="white"
      />
    </svg>
  );
}

function IconChevronRight(): React.ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={W.chevronW}
      height={W.chevronH}
      viewBox="0 0 11 18"
      fill="none"
      aria-hidden
    >
      <path
        d="M1.99548 17.1028L10.5469 8.5514L1.99548 0L0.000148773 1.99533L6.55622 8.5514L0.000148773 15.1075L1.99548 17.1028Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconChevronLeft(): React.ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={W.chevronW}
      height={W.chevronH}
      viewBox="0 0 11 18"
      fill="none"
      aria-hidden
    >
      <path
        d="M8.5514 17.1028L0 8.5514L8.5514 0L10.5467 1.99533L3.99065 8.5514L10.5467 15.1075L8.5514 17.1028Z"
        fill="currentColor"
      />
    </svg>
  );
}

const GalleryPicture: React.FC<{
  imageId: string;
  source: string;
  width: number;
  height: number;
  background?: ImgixBG;
  fit: ImgixFit;
}> = ({ imageId, source, width, height, background, fit }) => {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    setFailed(false);
  }, [imageId, source]);
  return (
    <Picture
      source={failed ? undefined : source}
      width={width}
      height={height}
      background={background}
      fit={fit}
      onError={() => setFailed(true)}
    />
  );
};

export const ConditionNotesPhotoGalleryDialog: React.FC<{
  images: ImageFragment[];
  open: boolean;
  startIndex: number;
  onClose(): void;
}> = ({ images, open, startIndex, onClose }) => {
  const [idx, setIdx] = React.useState(startIndex);
  React.useEffect(() => {
    if (!open) return;
    const last = images.length - 1;
    if (last < 0) {
      setIdx(0);
      return;
    }
    setIdx(Math.max(0, Math.min(startIndex, last)));
  }, [open, startIndex, images.length]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(images.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, images.length]);

  if (!open || images.length === 0) return null;
  if (typeof document === 'undefined') return null;

  const last = images.length - 1;
  const clampedIdx = Math.max(0, Math.min(idx, last));
  const at = images[clampedIdx]!;
  const canPrev = clampedIdx > 0;
  const canNext = clampedIdx < last;

  return createPortal(
    <>
      <style>{CSS}</style>
      <div role="dialog" aria-modal className="pedgal-overlay" aria-label="Condition photos" onClick={onClose}>
        <div className="pedgal-shell" onClick={(e) => e.stopPropagation()}>
          <div className="pedgal-top">
            <button
              type="button"
              className="pedgal-close"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="Close"
            >
              <IconClose />
            </button>
          </div>

          <div className="pedgal-stage">
            <div className="pedgal-stageInner">
              <GalleryPicture
                imageId={at.id}
                source={at.source}
                width={W.mainW}
                height={W.mainH}
                background={ImgixBG.White}
                fit={ImgixFit.Fill}
              />
            </div>
          </div>

          {images.length > 1 ? (
            <div className="pedgal-navRow">
              <button
                type="button"
                className="pedgal-navBtn pedgal-navBtn--prev"
                aria-label="Previous photo"
                disabled={!canPrev}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!canPrev) return;
                  setIdx((i) => Math.max(0, i - 1));
                }}
              >
                <IconChevronLeft />
              </button>
              <button
                type="button"
                className="pedgal-navBtn pedgal-navBtn--next"
                aria-label="Next photo"
                disabled={!canNext}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!canNext) return;
                  setIdx((i) => Math.min(last, i + 1));
                }}
              >
                <IconChevronRight />
              </button>
            </div>
          ) : null}

          <div className="pedgal-thumbs">
            {images.map((img, i) => {
              const active = i === clampedIdx;
              return (
                <button
                  type="button"
                  key={img.id}
                  className={`pedgal-thumb${active ? ' pedgal-thumb--active' : ' pedgal-thumb--idle'}`}
                  aria-label={`Photo ${i + 1} of ${images.length}`}
                  aria-current={active ? 'true' : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIdx(i);
                  }}
                >
                  <GalleryPicture
                    imageId={img.id}
                    source={img.source}
                    width={W.thumbW * 2}
                    height={W.thumbH * 2}
                    background={active ? ImgixBG.White : ImgixBG.Gray}
                    fit={ImgixFit.Max}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};
